import { MandateStatus } from "creso-core/constants";
import { useMandates, usePlanStartDate } from "creso-core/hooks";
import { XSIPTransactionOrder } from "creso-core/types";
import { formatDate, isBefore } from "date-fns";
import { useEffect, useMemo, useState } from "react";

type StaleErrorType = "REJECTED_MANDATE" | "EXPIRED_START_DATE";

type StaleError = { type: StaleErrorType; message: string };

export function useCheckStaleSip(sipOrder?: XSIPTransactionOrder) {
  const [errors, setErrors] = useState<StaleError[]>([]);

  const [minDate, getStartDate] = usePlanStartDate(
    {
      planType: "XSIP",
      frequency: sipOrder?.frequency ?? "MONTHLY",
      firstOrderToday: sipOrder?.first_order_today ?? true,
    },
    { enabled: !!sipOrder?.id }
  );

  const { mandates } = useMandates(
    sipOrder?.investment_account as string,
    !!sipOrder?.investment_account
  );

  useEffect(() => {
    if (sipOrder) {
      const errors: StaleError[] = [];
      if (minDate && isBefore(new Date(sipOrder.start_date), minDate)) {
        errors.push({
          type: "EXPIRED_START_DATE",
          message: `Start date cannot be before ${formatDate(minDate, "dd MMM yyyy")}`,
        });
      }
      const mandate = mandates?.find((m) => m.id === sipOrder.mandate_id);
      if (mandate?.status === MandateStatus.REJECTED) {
        errors.push({
          type: "REJECTED_MANDATE",
          message: `Mandate ${mandate.bank_account_details?.bank_name} *${mandate.bank_account_number.slice(-4)} for ${mandate.mandate_limit} has been rejected.`,
        });
      }
      setErrors(errors);
    }
  }, [sipOrder, minDate, mandates]);

  return useMemo(
    () => ({
      isStale: !!errors.length,
      errors,
      fix: () => {
        if (!sipOrder) return sipOrder;
        const newSipOrder = { ...sipOrder };
        if (errors.find((e) => e.type === "EXPIRED_START_DATE"))
          newSipOrder.start_date = getStartDate(newSipOrder.start_date);
        if (errors.find((e) => e.type === "REJECTED_MANDATE"))
          // @ts-expect-error ignore
          newSipOrder.mandate_id = null;
        return newSipOrder;
      },
    }),
    [errors, sipOrder, getStartDate]
  );
}
